// ponytail: To/From School is inferred by convention — trip types carry no
// direction field (see EditInstitutionModal DEFAULT_TRIP_TYPE_OBJECTS: am/pm/eca).
// AM is the only inbound (to-school) session; PM/ECA and any custom sessions are
// outbound. Upgrade path: add a `direction` to the trip type object and read it here.
const TO_SCHOOL_SESSIONS = new Set(["am"]);

/**
 * Group the sessions a child rides by direction, for badge display.
 * @param {Array<{session?: string}>} stops - e.g. child.associatedStops
 * @param {Array<{id: string, name: string}>} tripTypes
 * @returns {Array<{label: string, sessions: string[]}>} ordered lines, e.g.
 *   [{label: "To School", sessions: ["am"]}, {label: "From School", sessions: ["pm", "eca"]}]
 */
export const groupScheduledSessions = (stops, tripTypes) => {
  // Coerce null/undefined (defaults only cover undefined) — data may arrive null.
  const safeStops = stops || [];
  const safeTypes = tripTypes || [];
  const order = (id) => {
    const i = safeTypes.findIndex((t) => t.id === id);
    return i === -1 ? 999 : i;
  };

  let ids = [...new Set(safeStops.map((s) => s?.session).filter(Boolean))];
  // "none" = All Trip Sessions → the child rides every trip type.
  if (ids.includes("none")) ids = safeTypes.map((t) => t.id);

  const toSchool = ids.filter((id) => TO_SCHOOL_SESSIONS.has(id));
  const fromSchool = ids
    .filter((id) => !TO_SCHOOL_SESSIONS.has(id))
    .sort((a, b) => order(a) - order(b));

  const lines = [];
  if (toSchool.length) lines.push({ label: "To School", sessions: toSchool });
  if (fromSchool.length)
    lines.push({ label: "From School", sessions: fromSchool });
  return lines;
};

/**
 * The trip direction a child rides, for the mobile family card. Two-Way when
 * the child rides both directions — an "all trip sessions" (none) stop, or
 * stops spanning to-school and from-school (e.g. AM + PM). One-Way for a single
 * direction. Returns null when there are no stops.
 * @param {Array<{session?: string}>} stops - e.g. child.associatedStops
 * @param {Array<{id: string, name: string}>} tripTypes
 * @returns {"One-Way" | "Two-Way" | null}
 */
export const getTripDirection = (stops = [], tripTypes = []) => {
  if (!stops || !stops.length) return null;
  const bothDirections =
    stops.some((s) => s.session === "none") ||
    groupScheduledSessions(stops, tripTypes).length >= 2;
  return bothDirections ? "Two-Way" : "One-Way";
};
